export interface GuideArticle {
  id: string;
  title: string;
  summary: string;
  adminOnly?: boolean;
  blocks: { title: string; paragraphs?: string[]; steps?: string[] }[];
  links: { label: string; to: string }[];
}
export const adminGuide: GuideArticle[] = [
  {
    id: 'revisar', title: 'Revisar una entrega',
    summary: 'Abre el archivo correcto, comprueba su contenido y registra una decisión.',
    blocks: [
      { title: 'Abrir y comprobar', steps: [
        'En Revisar, busca la entrega y abre el documento. Comprueba la persona, el tipo de documento y el periodo del archivo.',
        'Lee el PDF con los controles de página y zoom. Si no se puede mostrar, abre el original. Para otros formatos, descarga el archivo desde Archivo y versiones.',
        'Compara el contenido con lo solicitado para ese tipo de documento. La fecha de carga y el periodo al que corresponde pueden ser distintos.',
      ] },
      { title: 'Registrar la decisión', paragraphs: [
        'Aprobar documento y Rechazar documento aparecen encima del archivo cuando esa versión admite una decisión. Comprueba el documento seleccionado antes de confirmar.',
        'Para rechazar, escribe un motivo concreto: qué dato falta, qué no corresponde o qué necesita corregirse. Espera el resultado del guardado antes de continuar.',
        'Solicitar revisión está disponible cuando el estado del archivo lo permite. Registra el cambio solicitado en el motivo; no equivale a recibir una nueva versión.',
      ] },
      { title: 'Continuar con otra entrega', paragraphs: [
        'Siguiente pendiente aparece cuando hay otro archivo pendiente dentro de los filtros del expediente. Cambia de documento; no guarda una decisión ni aprueba automáticamente.',
        'Usa Volver a resultados cuando hayas llegado desde una lista con contexto de retorno. Si otro revisor cambió el archivo, actualiza y comprueba su estado antes de intentar guardar de nuevo.',
      ] },
    ], links: [{ label: 'Abrir revisión de documentos', to: '/review' }],
  },
  {
    id: 'expediente', title: 'Periodos y versiones',
    summary: 'Distingue la cobertura de un requisito de los archivos que se han entregado.',
    blocks: [
      { title: 'Cobertura anual y documentos del periodo', paragraphs: [
        'Cobertura anual muestra los requisitos por mes. Selecciona una celda para consultar el periodo y, cuando la celda identifica un archivo, abrir esa entrega.',
        'Documentos del periodo permite elegir un requisito en la lista lateral. Puedes buscar por nombre, filtrar por categoría o mostrar solo los archivos por revisar.',
        'Un requisito puede abarcar varios meses según su periodicidad. No interpretes cada celda como un archivo distinto.',
      ] },
      { title: 'Consultar otra versión', steps: [
        'Abre Archivo y versiones para consultar el nombre del archivo, sus fechas y la descarga.',
        'Si hay varias entregas, elige una en Historial de archivos. La versión seleccionada queda identificada.',
        'Si aparece el aviso de versión anterior, comprueba que esa es la entrega que necesitas. Las acciones disponibles dependen de su estado y tus permisos.',
      ] },
      { title: 'Entender los estados', paragraphs: [
        'Pendiente de carga: no hay un archivo para consultar. Pendiente de aprobación: la entrega espera una decisión. Revisión solicitada: hay un cambio solicitado registrado.',
        'Aprobado y Rechazado describen el estado del archivo. El motivo registrado explica el rechazo o los cambios solicitados. Una versión sustituida o un documento histórico puede quedar solo para consulta.',
      ] },
      { title: 'Opciones del requerimiento', paragraphs: [
        'Las opciones secundarias incluyen la periodicidad y, cuando corresponde, aprobar sin documento, descartar un pendiente duplicado o eliminar un archivo rechazado. No sustituyen la revisión de una entrega.',
        'Aprobar sin documento solo aparece en requisitos opcionales que admiten esa acción. Eliminar un archivo rechazado es una opción administrativa; revisa la confirmación antes de continuar.',
      ] },
    ], links: [{ label: 'Buscar un expediente', to: '/admin/users' }],
  },
  {
    id: 'datos-detectados', title: 'Datos detectados',
    summary: 'Consulta los valores extraídos y contrástalos con el archivo original.',
    blocks: [
      { title: 'Consultar la evidencia', steps: [
        'Dentro del documento, selecciona Consultar datos detectados.',
        'Revisa los campos disponibles para esa definición. Cuando hay evidencia, consulta el texto de origen y usa Ver página para ir a la página indicada del PDF.',
        'Comprueba cualquier diferencia con la persona, el periodo o el contenido esperado antes de decidir.',
      ] },
      { title: 'Qué significa una extracción', paragraphs: [
        'Los datos detectados son una ayuda de lectura. No certifican autenticidad, vigencia ni cumplimiento, y no aprueban ni rechazan archivos.',
        'Los campos dependen del tipo de documento. Un campo ausente no prueba por sí mismo que el documento sea incorrecto: revisa el original.',
        'El panel es de consulta. Actualmente no permite guardar correcciones de los valores detectados ni presenta un porcentaje de certeza calibrado.',
      ] },
      { title: 'Archivos en proceso o sin extracción', paragraphs: [
        'Puedes revisar el documento mientras está en cola o procesándose. Si aparece revisión manual, un error o no hay datos disponibles, continúa con el archivo original.',
        'Algunos formatos, como archivos nativos .SUA, requieren revisión manual. La ausencia de extracción no bloquea la decisión del revisor.',
      ] },
    ], links: [{ label: 'Abrir revisión de documentos', to: '/review' }],
  },
  {
    id: 'usuarios', title: 'Encontrar una persona',
    summary: 'Accede al expediente desde Usuarios y distingue documentos de administración de cuenta.',
    blocks: [
      { title: 'Abrir sus documentos', steps: [
        'En Usuarios, busca por nombre o correo. Usa los filtros si necesitas acotar la lista.',
        'Comprueba la organización y selecciona Documentos en la fila de la persona.',
        'Dentro del expediente, elige la cobertura anual o un periodo para consultar las entregas.',
      ] },
      { title: 'Elegir la apariencia', paragraphs: ['El selector de la barra superior permite elegir Claro, Oscuro o Sistema. Sistema sigue la apariencia del dispositivo. La elección se conserva en este navegador; los documentos mantienen sus colores originales.'] },
      { title: 'Datos de cuenta', paragraphs: [
        'Cuenta reúne los datos y acciones de administración de la persona. Las opciones disponibles dependen de tu rol. Abrir Documentos no cambia su cuenta.',
        'Categorías y Definiciones son catálogos administrativos y están al final de la navegación, encima de las opciones de cuenta.',
      ] },
    ], links: [{ label: 'Abrir Usuarios', to: '/admin/users' }],
  },
  {
    id: 'resumen', title: 'Leer el resumen',
    summary: 'Interpreta los indicadores sin confundir actividad, aprobación y cobertura.',
    blocks: [
      { title: 'Indicadores de revisión', paragraphs: [
        'Pendientes de revisión es el conteo actual. Recibidos y revisados compara las cargas con las aprobaciones registradas en las últimas ocho semanas completas.',
        'Las barras de revisados incluyen archivos actualmente aprobados con fecha de aprobación. Los rechazos no se incluyen en ese gráfico porque su fecha de decisión todavía no es fiable para esta comparación.',
        'Tiempo de revisión muestra la mediana semanal entre carga y aprobación. El valor destacado corresponde a la última semana completa. No incluye tiempos negativos ni registros sin fechas válidas; una semana sin datos queda sin punto.',
        'Pasa el cursor por una semana para ver sus valores. También puedes tocar el gráfico o recorrer sus puntos con el teclado. Escape cierra el detalle.',
      ] },
      { title: 'Cobertura por organización', paragraphs: [
        'La cobertura resume porcentajes mensuales del reporte. Su filtro de año y organización se aplica a ese bloque, no a toda la página.',
        'No es el número de archivos subidos ni una medida de puntualidad. Puede incluir requisitos aprobados sin archivo. Consulta Cómo se calcula la cobertura para ver el alcance.',
      ] },
      { title: 'Actividad del periodo', paragraphs: [
        'El selector Hoy, Últimos 7 días o Último mes corresponde al bloque de actividad. No cambia las ocho semanas completas de los gráficos superiores.',
      ] },
    ], links: [{ label: 'Abrir Resumen', to: '/admin/dashboard' }],
  },
  {
    id: 'cumplimiento', title: 'Consultar cumplimiento', adminOnly: true,
    summary: 'Consulta el porcentaje mensual por persona y exporta la selección.',
    blocks: [
      { title: 'Consultar el reporte', steps: [
        'Abre Reporte y selecciona el año. Puedes filtrar por organización o seleccionar varias personas.',
        'Lee el porcentaje de Cumplimiento de cada mes completo. El mes actual y los futuros permanecen en blanco.',
        'Selecciona un porcentaje para abrir el expediente de esa persona en el mes indicado.',
      ] },
      { title: 'Interpretar y exportar', paragraphs: [
        '0% es un valor de cumplimiento. Un guion indica que el dato no está disponible; no equivale a cero. El reporte ofrece únicamente la vista Cumplimiento.',
        'Exportar PDF utiliza las personas filtradas, el año y los mismos porcentajes del reporte. La exportación no está disponible durante una carga, ante un error o si no hay resultados.',
        'Actualizar solicita el recálculo y vuelve a cargar el reporte. Cambiar de año o abrir una celda no ejecuta ese recálculo.',
      ] },
    ], links: [{ label: 'Abrir Reporte', to: '/report' }],
  },
  {
    id: 'actividad', title: 'Consultar actividad',
    summary: 'Localiza una acción y abre el documento relacionado.',
    blocks: [
      { title: 'Acciones sobre documentos', paragraphs: [
        'Actividad reúne cargas, aprobaciones, rechazos y otros eventos disponibles. Cuando el registro tiene un documento asociado, puedes abrirlo para comprobar su estado.',
        'Este historial se obtiene de los registros actuales y no es una auditoría inmutable de todos los cambios. Una edición o un cambio de estado puede modificar lo que aparece.',
      ] },
      { title: 'Decisión y notificación', paragraphs: [
        'Guardar una decisión y enviar una notificación son operaciones distintas. Si se informa que la decisión fue guardada pero no se confirmó el envío, comprueba el estado del documento antes de repetir la acción.',
        'El historial de notificaciones de un expediente está en Más opciones. Describe envíos, no sustituye el estado ni el historial de decisiones.',
        'La configuración general de notificaciones está oculta y no está disponible como función de administración.',
      ] },
    ], links: [{ label: 'Abrir Actividad', to: '/admin/activity' }],
  },
  {
    id: 'catalogos', title: 'Categorías y definiciones', adminOnly: true,
    summary: 'Mantén los tipos de documentos y su organización.',
    blocks: [
      { title: 'Categorías', paragraphs: [
        'Las categorías organizan las definiciones que aparecen en las listas y filtros. Se administran desde Categorías, al final de la navegación.',
      ] },
      { title: 'Definiciones', paragraphs: [
        'Una definición describe el tipo de documento, su categoría, periodicidad y obligatoriedad. Es distinta de un archivo entregado por una persona.',
        'Busca la definición y revisa sus valores antes de editarla. No cambies el catálogo para resolver un rechazo de un archivo individual: registra el motivo en la revisión de esa entrega.',
        'Para una periodicidad particular de un requisito, consulta las opciones de ese requisito en el expediente. No asumas que editar el catálogo equivale a editar todos los archivos existentes.',
      ] },
    ], links: [{ label: 'Abrir Categorías', to: '/admin/categories' }, { label: 'Abrir Definiciones', to: '/admin/definitions' }],
  },
  {
    id: 'problemas', title: 'Resolver un problema',
    summary: 'Comprueba el contexto y el estado antes de repetir una operación.',
    blocks: [
      { title: 'No se encuentra el documento del enlace', paragraphs: [
        'Comprueba la persona y el periodo de la dirección. Un enlace a un archivo concreto no selecciona otra versión automáticamente si no lo encuentra. Usa la lista de documentos para localizar la entrega correcta.',
      ] },
      { title: 'El PDF no se muestra', paragraphs: [
        'Prueba Reintentar PDF o abre el original en otra pestaña. Si el archivo requiere contraseña o tiene un formato distinto, revísalo con una aplicación compatible.',
      ] },
      { title: 'No aparecen acciones de aprobación', paragraphs: [
        'Comprueba el estado y la versión seleccionada. Las acciones dependen de tus permisos y de si el archivo admite una decisión; un documento histórico puede quedar solo para consulta.',
      ] },
      { title: 'Un dato no carga o una decisión no se guarda', paragraphs: [
        'Usa la opción de reintento indicada por el mensaje. Si otro revisor modificó el documento, actualiza y comprueba el estado antes de decidir otra vez.',
        'Si necesitas reportar el problema, conserva el enlace al expediente, el periodo y el mensaje mostrado. No incluyas contraseñas ni claves de acceso.',
      ] },
    ], links: [],
  },
];
export const guideText = (article: GuideArticle) => [article.title, article.summary, ...article.blocks.flatMap(block => [block.title, ...(block.paragraphs || []), ...(block.steps || [])])].join(' ');
export const normalizeGuideSearch = (value: string) => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase().trim();
